import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  EMPLOYER_CHANGE_ALERT_EMAIL_PREVIEW_QUERY,
  type EmailPreview,
  type EmployerChangeAlertEmailPreviewQueryResponse,
  type EmployerChangeAlertEmailPreviewQueryVariables,
  type EmployerChangeAlertEmailPreviewVariables
} from "../_queries/emailPreviewQueries";

export function useEmployerChangeAlertEmailPreview() {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    EmployerChangeAlertEmailPreviewQueryResponse,
    EmployerChangeAlertEmailPreviewQueryVariables
  >(EMPLOYER_CHANGE_ALERT_EMAIL_PREVIEW_QUERY, { fetchPolicy: "network-only" });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (params: EmployerChangeAlertEmailPreviewVariables) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: {
          personId: params.personId,
          facilityId: params.facilityId,
          jobTitle: params.jobTitle,
          startedOn: params.startedOn,
          endedOn: params.endedOn ?? null,
          careerConsultantId: params.careerConsultantId ?? null
        }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.employerChangeAlertEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
